import { Injectable } from "@angular/core";
import { Resolve, ActivatedRouteSnapshot } from "@angular/router";
import { Observable } from "rxjs";
import {
  RequestService,
  RequestDetails,
  RequestDesign,
  RequestInformation,
  TissueCollectionType,
  Active,
  Adjuvant
} from "../../shared/services/request.service";
import {
  ViewableRequest,
  RequestedSeed,
  Treatment,
  PottingInstructions,
  PlantingInstructions,
  PollinationInstructions,
  PlantHandlingInstructions,
  FlatInstructions,
  RequestedSeedTreatment,
  RequestedSampling,
  PollinationPreferenceGroup,
  PollinationPreferenceGroupItem,
  SprayBoothStudyInstructions
} from "../review-request/review-request.model";
import { asEnumerable, Enumerable } from "linq-es2015";
import { CeSiteService, CeSite } from "../../shared/services/ce-site.service";
import { CropService, Crop } from "../../shared/services/crop.service";
import { RequestPurposeService, RequestPurpose } from "../../shared/services/request-purpose.service";
import { ProductConceptService, ProductConcept } from "../../shared/services/product-concept.service";
import { SubstrateService, Substrate } from "../../shared/services/substrate.service";
import { IrrigationMethodService, IrrigationMethod } from "../../shared/services/irrigation-method.service";
import { FertigationMethodService, FertigationMethod } from "../../shared/services/fertigation-method.service";
import { LightingInstructionService, LightingInstruction } from "../../shared/services/lighting-instruction.service";
import { TemperatureInstructionService, TemperatureInstruction } from "../../shared/services/temperature-instruction.service";
import { HumidityInstructionService, HumidityInstruction } from "../../shared/services/humidity-instruction.service";
import { PesticideInstructionService, PesticideInstruction } from "../../shared/services/pesticide-instruction.service";
import { PollinationCategoryService, PollinationCategory } from "../../shared/services/pollination-category.service";
import { ContainerDefinitionService, ContainerDefinition } from "../../shared/services/container-definition.service";
import { PmoProgramService, PmoProjectsQueryModel, PmoProgram, PmoProject } from "../../shared/services/pmo-program.service";
import { UserService, User } from "../../shared/services/user.service";
import { PollinationTypeService, PollinationType } from "../../shared/services/pollination-type.service";
import { Laboratory, LaboratoryService } from "../../shared/services/laboratory.service";
import { TissueType, TissueTypeService } from "../../shared/services/tissue-type.service";
import { GrowthStage, GrowthStageService } from "../../shared/services/growth-stage.service";
import { ConjunctionEnum } from "../../shared/models/conjunction.enum";
import { PollinationStakeLabelItemData, PollinationStakeLabelService } from "../../shared/services/pollination-stake-label.service";
import { ActiveModel, AdjuvantModel, AdjuvantUnit, ApplicationTimepoint, ApplicationVolume, RateUnit, SprayBoothStudyService } from "../../shared/services/spray-booth-study.service";

@Injectable()
export class RequestReviewResolver implements Resolve<ViewableRequest> {
  private id: number;
  private requestInformation: RequestInformation;
  private details: RequestDetails;
  private design: RequestDesign;
  private config: ConfigurationData;
  private pmoProject: string = null;
  private request: ViewableRequest;

  constructor(
    private requestService: RequestService,
    private userService: UserService,
    private ceSiteService: CeSiteService,
    private cropService: CropService,
    private requestPurposeService: RequestPurposeService,
    private pmoProgramService: PmoProgramService,
    private productConceptService: ProductConceptService,
    private substrateService: SubstrateService,
    private irrigationMethodService: IrrigationMethodService,
    private fertigationMethodService: FertigationMethodService,
    private lightingInstructionService: LightingInstructionService,
    private temperatureInstructionService: TemperatureInstructionService,
    private humidityInstructionService: HumidityInstructionService,
    private pesticideInstructionService: PesticideInstructionService,
    private pollinationCategoryService: PollinationCategoryService,
    private containerDefinitionService: ContainerDefinitionService,
    private pollinationTypeService: PollinationTypeService,
    private labService: LaboratoryService,
    private growthStageService: GrowthStageService,
    private tissueTypeService: TissueTypeService,
    private stakeLabelService: PollinationStakeLabelService,
    private sprayStudyService: SprayBoothStudyService
  ) { }

  resolve(route: ActivatedRouteSnapshot): Observable<ViewableRequest> {
    this.id = route.params["id"];

    return Observable.forkJoin(
      this.requestService.getRequestInformation(this.id),
      this.requestService.getDetails(this.id),
      this.requestService.getDesign(this.id),
      this.userService.getAll(),
      this.ceSiteService.getAll(),
      this.cropService.getAll(),
      this.requestPurposeService.getAll(),
      this.pmoProgramService.getAllPrograms(),
      this.productConceptService.getAll(),
      this.substrateService.getAll(),
      this.irrigationMethodService.getAll(),
      this.fertigationMethodService.getAll(),
      this.lightingInstructionService.getAll(),
      this.temperatureInstructionService.getAll(),
      this.humidityInstructionService.getAll(),
      this.pesticideInstructionService.getAll(),
      this.pollinationCategoryService.getAll(),
      this.containerDefinitionService.getFlats(),
      this.containerDefinitionService.getPots(),
      this.pollinationTypeService.getAll(),
      this.labService.getAll(),
      this.growthStageService.getAll(),
      this.tissueTypeService.getAll(),
      this.sprayStudyService.getAllActives(),
      this.sprayStudyService.getAllAdjuvantUnits(),
      this.sprayStudyService.getAllAdjuvants(),
      this.sprayStudyService.getAllTimepoints(),
      this.sprayStudyService.getAllVolumes(),
      this.sprayStudyService.getAllRateUnits()
    ).switchMap(
      ([
        requestInformation,
        details,
        design,
        users,
        cesites,
        crops,
        requestpurposes,
        pmoPrograms,
        productconcepts,
        substrates,
        irrigationMethods,
        fertigationMethods,
        lightingInstructions,
        temperatureInstructions,
        humidityInstructions,
        pesticideInstructions,
        pollinationCategories,
        flatTypes,
        potTypes,
        pollinationTypes,
        labs,
        growthStages,
        tissueTypes,
        actives,
        adjuvantUnits,
        adjuvants,
        applicationTimepoints,
        applicationVolumes,
        rateUnits
      ]) => {
        this.details = details || {};
        this.design = design || {};
        this.requestInformation = requestInformation || {};
        this.config = {
          users: asEnumerable(users),
          ceSites: asEnumerable(cesites),
          crops: asEnumerable(crops),
          requestPurposes: asEnumerable(requestpurposes),
          pmoPrograms: asEnumerable(pmoPrograms),
          productConcepts: asEnumerable(productconcepts),
          substrates: asEnumerable(substrates),
          irrigationMethods: asEnumerable(irrigationMethods),
          fertigationMethods: asEnumerable(fertigationMethods),
          lightingInstructions: asEnumerable(lightingInstructions),
          temperatureInstructions: asEnumerable(temperatureInstructions),
          humidityInstructions: asEnumerable(humidityInstructions),
          pesticideInstructions: asEnumerable(pesticideInstructions),
          pollinationCategories: asEnumerable(pollinationCategories),
          flatTypes: asEnumerable(flatTypes),
          potTypes: asEnumerable(potTypes),
          pollinationTypes: asEnumerable(pollinationTypes),
          growthStages: asEnumerable(growthStages),
          laboratories: asEnumerable(labs),
          tissueTypes: asEnumerable(tissueTypes),
          actives: asEnumerable(actives),
          adjuvantUnits: asEnumerable(adjuvantUnits),
          adjuvants: asEnumerable(adjuvants),
          applicationTimepoints: asEnumerable(applicationTimepoints),
          applicationVolumes: asEnumerable(applicationVolumes),
          rateUnits: asEnumerable(rateUnits)
        };

        return Observable.zip(
          this.details.projectKey ? this.pmoProgramService.getProjects(<PmoProjectsQueryModel>{ pmoProgramKey: this.details.programKey }) : Observable.of(null)
        );
      }).map(([pmoprojects]) => {
        const projects: Enumerable<PmoProject> = asEnumerable(pmoprojects);
        this.pmoProject = this.details.projectKey ? projects.Single(p => p.projectKey === this.details.projectKey).pmoProjectName : null;

        this.formatRequest();

        return this.request;
      });
  }

  private formatRequest(): void {
    let requestedSeed: RequestedSeed[] = null;
    if (this.design.requestedSeed && this.design.requestedSeed.length > 0) {
      requestedSeed = asEnumerable(this.design.requestedSeed)
        .Select(s => <RequestedSeed>{
          entryNumber: s.entryNumber,
          siid: s.siid,
          geid: s.geid,
          geName: s.geName,
          numberOfSeeds: s.numberOfSeeds,
          regulatoryStatus: s.regulatoryStatus,
          containsColorMarker: s.containsColorMarker,
          treatments: asEnumerable(s.treatments)
            .Select(t => <RequestedSeedTreatment>{
              treatmentNumber: t.treatmentNumber,
              seedCount: t.seedCount
            }).ToArray()
        }).ToArray()
    }

    let pollinationInstructions: PollinationInstructions = null;
    if (this.design.pollinationInstructions) {
      const hasCategory = this.design.pollinationInstructions.pollinationCategoryId != null;
      
      const items = asEnumerable(this.design.pollinationInstructions.preferences)
        .SelectMany((g, groupIndex) =>
          asEnumerable(g.items)
            .Select((i, itemIndex) => <PollinationStakeLabelItemData>{
              groupIndex: groupIndex,
              groupConjunction: this.stakeLabelService.getGroupConjunction(groupIndex, g.conjunction, hasCategory),
              itemIndex: itemIndex,
              itemConjunction: itemIndex === 0 ? "" : (i.conjunction === ConjunctionEnum.And ? "&" : "/"),
              pollinationType: i.pollinationTypeId,
              geName: i.geName,
              truncatedPollinationType: ""
            })
        )
        .ToArray();

      const pollinationCategoryName = this.design.pollinationInstructions.pollinationCategoryId ?
        this.config.pollinationCategories.Single(c => c.id === this.design.pollinationInstructions.pollinationCategoryId).name :
        null;

      pollinationInstructions = {
        preferences: asEnumerable(this.design.pollinationInstructions.preferences)
          .OrderBy(g => g.orderNumber)
          .Select((g, index) => <PollinationPreferenceGroup>{
            order: g.orderNumber,
            conjunction: g.conjunction === ConjunctionEnum.Unknown ? '' : ConjunctionEnum[g.conjunction],
            items: asEnumerable(g.items)
              .Select(item => <PollinationPreferenceGroupItem>{
                order: item.orderNumber,
                conjunction: item.conjunction === ConjunctionEnum.Unknown ? '' : ConjunctionEnum[item.conjunction],
                pollinationType: this.config.pollinationTypes.Single(t => t.id === item.pollinationTypeId).name,
                geName: item.geName
              })
              .ToArray()
          })
          .ToArray(),
        pollinationCategory: pollinationCategoryName,
        stakeLabelString: this.stakeLabelService.createStakeLabel(items, this.design.pollinationInstructions.pollinationCategoryId, pollinationCategoryName),
        donorsNeeded: this.design.pollinationInstructions.donorsNeeded
      }
    }

    let samplings: RequestedSampling[] = null;

    if (this.design.requestedSamplings) {
      samplings = [];
      this.design.requestedSamplings.forEach(s => {
        let laboratory = s.laboratoryId ? this.config.laboratories.SingleOrDefault(l => l.id === s.laboratoryId) : null;
        let labName = laboratory ? laboratory.name : null;
        let assay = s.laboratoryAssayId ? asEnumerable(laboratory.assays).Single(l => l.id === s.laboratoryAssayId).name : null;
        let tissueType = s.tissueTypeId ? this.config.tissueTypes.SingleOrDefault(l => l.id === s.tissueTypeId).name : null;

        let tissueCollectionType = TissueCollectionType[s.tissueCollectionType];
        let growthStage = s.growthStageId !== null ? this.config.growthStages.SingleOrDefault(l => l.id === s.growthStageId).name : null;

        samplings.push({
          assay: assay,
          laboratory: labName,
          tissueType: tissueType,
          tissueCollectionType: tissueCollectionType,
          growthStage: growthStage,
          daysAfterPlanting: s.daysAfterPlanting,
          order: s.order
        });
      });
    }


    let treatments: Treatment[] = null;
    if (this.design.treatments && this.design.treatments.length > 0) {
      let sprayBoothStudies: SprayBoothStudyInstructions[] = null;
      treatments = [];
      this.design.treatments.forEach(t => {
        const plantingInstructions: PlantingInstructions = {
          treatmentNumber: t.treatmentNumber,
          seedsToStart: t.plantingInstructions.seedsToStart,
          plantsToPots: t.plantingInstructions.plantsToPots,
          plantsToHarvest: t.plantingInstructions.plantsToHarvest
        };

        const flatInstructions: FlatInstructions = {
          treatmentNumber: t.treatmentNumber,
          directSeedToPots: t.flatInstructions.directSeedToPots,
          flatType: t.flatInstructions.flatTypeId ? this.config.flatTypes.Single(c => c.id === t.flatInstructions.flatTypeId).name : null,
          substrate: t.flatInstructions.substrateId ? this.config.substrates.Single(s => s.id === t.flatInstructions.substrateId).name : null,
          irrigationMethod: t.flatInstructions.irrigationMethodId ? this.config.irrigationMethods.Single(m => m.id === t.flatInstructions.irrigationMethodId).name : null,
          fertigationMethod: t.flatInstructions.fertigationMethodId ? this.config.fertigationMethods.Single(m => m.id === t.flatInstructions.fertigationMethodId).name : null
        };


        const pottingInstructions: PottingInstructions = {
          treatmentNumber: t.treatmentNumber,
          potType: t.pottingInstructions && t.pottingInstructions.potTypeId ? this.config.potTypes.Single(c => c.id === t.pottingInstructions.potTypeId).name : null,
          substrate: t.pottingInstructions && t.pottingInstructions.substrateId ? this.config.substrates.Single(s => s.id === t.pottingInstructions.substrateId).name : null,
          irrigationMethod: t.pottingInstructions && t.pottingInstructions.irrigationMethodId ? this.config.irrigationMethods.Single(m => m.id === t.pottingInstructions.irrigationMethodId).name : null,
          fertigationMethod: t.pottingInstructions && t.pottingInstructions.fertigationMethodId ? this.config.fertigationMethods.Single(m => m.id === t.pottingInstructions.fertigationMethodId).name : null
        }

        const plantHandlingInstructions: PlantHandlingInstructions = {
          treatmentNumber: t.treatmentNumber,
          lightingInstruction: t.plantHandlingInstructions.lightingInstructionId ? this.config.lightingInstructions.Single(i => i.id === t.plantHandlingInstructions.lightingInstructionId).name : null,
          temperatureInstruction: t.plantHandlingInstructions.temperatureInstructionId ? this.config.temperatureInstructions.Single(i => i.id === t.plantHandlingInstructions.temperatureInstructionId).name : null,
          humidityInstruction: t.plantHandlingInstructions.humidityInstructionId ? this.config.humidityInstructions.Single(i => i.id === t.plantHandlingInstructions.humidityInstructionId).name : null,
          imagingNeeded: t.plantHandlingInstructions.imagingNeeded,
        };

        if (t.sprayBoothStudy && Array.isArray(t.sprayBoothStudy)) {
          sprayBoothStudies = [];
          sprayBoothStudies = t.sprayBoothStudy.map((s) => ({
            actives: s.actives.map((active: Active) => {
              const herbicideTradeName = active.activeId
                ? this.config.actives.Single((i) => i.id === active.activeId)
                  .herbicideTradeName
                : null;
              const factsid = active.activeId
                ? this.config.actives.Single((i) => i.id === active.activeId)
                  .factsid
                : null;
              const activeIngredient = active.activeId
                ? this.config.actives.Single((i) => i.id === active.activeId)
                  .activeIngredient
                : null;
              const concentrationValue = active.activeId
                ? this.config.actives.Single((i) => i.id === active.activeId)
                  .concentrationValue
                : null;
              const concentrationUnit = active.activeId
                ? this.config.actives.Single((i) => i.id === active.activeId)
                  .concentrationUnit
                : null;
              const rateValue = active.rateValue;
              const rateUnit = active.rateUnitId
                ? this.config.rateUnits.Single(
                  (i) => i.id === active.rateUnitId
                ).unit
                : null;
              const relativeFieldRate = active.relativeFieldRate;
              return {
                herbicideTradeName: herbicideTradeName,
                factsid: factsid,
                activeIngredient: activeIngredient,
                concentrationValue: concentrationValue,
                concentrationUnit: concentrationUnit,
                rateValue: rateValue,
                rateUnit: rateUnit,
                relativeFieldRate: relativeFieldRate,
              };
            }),
            adjuvants: s.adjuvants.map((adjuvant: Adjuvant) => {
              const adjuvantName = adjuvant.adjuvantId
                ? this.config.adjuvants.Single(
                  (i) => i.id === adjuvant.adjuvantId
                ).name
                : null;
              const adjuvantValue = adjuvant.adjuvantValue;

              const adjuvantDef = adjuvant.adjuvantId
                ? this.config.adjuvants.Single(
                  (i) => i.id === adjuvant.adjuvantId
                )
                : null;

              const adjuvantUnit = adjuvantDef
                ? this.config.adjuvantUnits.Single(
                  (u) => u.id === adjuvantDef.adjuvantUnitId
                ).unit || null
                : null;

              return {
                adjuvantName: adjuvantName,
                adjuvantValue: adjuvantValue,
                adjuvantUnit: adjuvantUnit,
              };
            }),
            treatmentNumber: t.treatmentNumber,
            sprayEventNumber: s.sprayEventNumber,
            applicationVolume: s.applicationVolumeId
              ? this.config.applicationVolumes.Single(
                (i) => i.id === s.applicationVolumeId
              ).volume
              : null,
            applicationTimepoint: s.applicationTimepointId
              ? this.config.applicationTimepoints.Single(
                (i) => i.id === s.applicationTimepointId
              ).timepoint
              : null,
            sprayEventDaysAfterPlanting: s.sprayEventDaysAfterPlanting,
          }));
        }

        treatments.push({
          treatmentNumber: t.treatmentNumber,
          plantingInstructions: plantingInstructions,
          pottingInstructions: pottingInstructions,
          flatInstructions: flatInstructions,
          plantHandlingInstructions: plantHandlingInstructions,
          sprayBoothStudyInstructions: sprayBoothStudies
        });
      });
    }

    let requester = this.config.users.Single(u => u.id === this.details.requesterId);
    let requestDelegate = this.details.delegateId ? this.config.users.Single(u => u.id === this.details.delegateId) : null;

    this.request = {
      id: this.id,
      requestNumber: this.requestInformation.requestNumber,
      requester: `${requester.givenName} ${requester.surname}`,
      delegate: requestDelegate ? `${requestDelegate.givenName} ${requestDelegate.surname}` : "",
      title: this.details.title,
      description: this.details.description,
      regulatoryStatus: this.details.regulatoryStatus,
      ppqRequired: this.details.ppqRequired,
      phytoInspectionRequired: this.details.phytoInspectionRequired,
      ceSite: this.details.ceSiteId ? this.config.ceSites.Single(s => s.id === this.details.ceSiteId).name : null,
      crop: this.details.cropId ? this.config.crops.Single(s => s.id === this.details.cropId).name : null,
      cropId: this.details.cropId,
      requestPurpose: this.details.requestPurposeId ? this.config.requestPurposes.Single(s => s.id === this.details.requestPurposeId).name : null,
      pmoProgram: this.details.programKey ? this.config.pmoPrograms.Single(p => p.programKey === this.details.programKey).pmoProgramName : null,
      pmoProject: this.pmoProject,
      productConcepts: this.config.productConcepts.Join(this.details.productConceptIds,
        productConcept => productConcept.id,
        id => id,
        productConcept => ({ name: productConcept.name }))
        .Select(c => c.name)
        .ToArray()
        .join(", "),
      requestedPlantingDate: this.details.requestedPlantingDate,
      requestedSeedPacketDate: this.details.requestedSeedPacketDate,
      weeksOfColdTreatment: this.details.weeksOfColdTreatment,
      numberOfTreatments: treatments ? treatments.length : null,
      numberOfReps: this.design.numberOfReps,
      numberOfSeedsPerCell: this.design.numberOfSeedsPerCell,
      indexing: this.design.indexing,
      embryoRescue: this.design.embryoRescue,
      minimumSeedsToReturn: this.design.minimumSeedsToReturn,
      samplingNeeded: this.design.requestedSamplings && this.design.requestedSamplings.length ? true : false,
      statusId: this.requestInformation.statusId,
      forAdvancement: this.design.advancementPreference.forAdvancement,
      haploidInduction: this.design.advancementPreference.haploidInduction,
      manyToOne: this.design.advancementPreference.manyToOne,
      uniquePedigree: this.design.advancementPreference.uniquePedigree,
      pollinationInstructions: pollinationInstructions,
      recurrencePattern: this.design.recurrencePattern,
      recurrenceWeekInterval: this.design.recurrenceWeekInterval,
      recurrenceDays: this.design.recurrenceDays,
      recurrenceEndDate: this.design.recurrenceEndDate,
      totalSeedsToStart: treatments ? asEnumerable(treatments).Select(t => t.plantingInstructions.seedsToStart).Sum() : null,
      totalPlantsToPots: treatments ? asEnumerable(treatments).Select(t => t.plantingInstructions.plantsToPots).Sum() : null,
      totalPlantsToHarvest: treatments ? asEnumerable(treatments).Select(t => t.plantingInstructions.plantsToHarvest).Sum() : null,
      requestedSamplings: samplings,
      seedNeeds: requestedSeed,
      treatments: treatments,
      seedRequestDesired: this.design.seedRequestDesired,
      originatingSystem: this.design.originatingSystem,
      pesticideInstruction: this.design.pesticideInstructionId ? this.config.pesticideInstructions.Single(i => i.id === this.design.pesticideInstructionId).name : null,
      pesticidesAllowedAfterDays: this.design.pesticideInstructionId ? this.design.pesticidesAllowedAfterDays : null,
      pesticideLifeOfPlant: this.design.pesticideLifeOfPlant,
      displayPesticidesAllowedAfterDays: this.design.pesticideInstructionId ? this.config.pesticideInstructions.Single(i => i.id === this.design.pesticideInstructionId).requiresNumberOfDays : false,
    };
  }

}

export interface ConfigurationData {
  users: Enumerable<User>;
  ceSites: Enumerable<CeSite>;
  crops: Enumerable<Crop>;
  requestPurposes: Enumerable<RequestPurpose>;
  pmoPrograms: Enumerable<PmoProgram>;
  productConcepts: Enumerable<ProductConcept>;
  substrates: Enumerable<Substrate>;
  flatTypes: Enumerable<ContainerDefinition>;
  potTypes: Enumerable<ContainerDefinition>;
  irrigationMethods: Enumerable<IrrigationMethod>;
  fertigationMethods: Enumerable<FertigationMethod>;
  lightingInstructions: Enumerable<LightingInstruction>;
  temperatureInstructions: Enumerable<TemperatureInstruction>;
  humidityInstructions: Enumerable<HumidityInstruction>;
  pesticideInstructions: Enumerable<PesticideInstruction>;
  pollinationCategories: Enumerable<PollinationCategory>;
  pollinationTypes: Enumerable<PollinationType>;
  laboratories: Enumerable<Laboratory>;
  tissueTypes: Enumerable<TissueType>;
  growthStages: Enumerable<GrowthStage>;
  actives: Enumerable<ActiveModel>;
  applicationVolumes: Enumerable<ApplicationVolume>;
  applicationTimepoints: Enumerable<ApplicationTimepoint>;
  rateUnits: Enumerable<RateUnit>;
  adjuvants: Enumerable<AdjuvantModel>;
  adjuvantUnits: Enumerable<AdjuvantUnit>;
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/view-request/resolvers/request-review.resolver.ts